<template>
  <div class="wr-detail-dialog-root">
    <el-dialog
      title="记录详情"
      :visible="visible"
      width="800px"
      custom-class="premium-dialog"
      append-to-body
      :close-on-click-modal="false"
      top="5vh"
      @close="onClose"
    >
      <div v-if="detailData" class="detail-wrapper">
        <div class="detail-main v9-edition animate__animated animate__fadeIn">
          <div class="v9-header">
            <div class="v9-teacher-info">
              <h1>{{ detailData.Record.TeacherName }}</h1>
              <div class="v9-badges">
                <span
                  class="v9-badge-pill"
                  :class="detailData.Record.TeacherRole === 1 ? 'v9-bg-indigo' : 'v9-bg-emerald'"
                >
                  {{ detailData.Record.TeacherRole === 1 ? '班主任' : '任课教师' }}
                </span>
                <span class="v9-meta-dot"></span>
                <span class="v9-class-info">{{ detailData.Record.ClassName }}</span>
                <span class="v9-meta-dot"></span>
                <span class="v9-date-info" title="业务记录日期">
                  <i class="el-icon-date"></i>
                  {{ detailData.Record.RecordDate ? formatSimpleDate(detailData.Record.RecordDate) : '' }}
                </span>
                <span class="v9-meta-dot"></span>
                <span class="v9-date-info" title="系统提交时间">
                  <i class="el-icon-time"></i>
                  {{ detailData.Record.SubmitTime }}
                </span>
              </div>
            </div>
          </div>

          <div class="section-v9">
            <div class="v9-section-header">
              <span class="v9-accent"></span>
              <h3>工作内容</h3>
            </div>
            <div class="v9-card work-items-card">
              <div class="v9-chip-container">
                <span v-for="item in detailData.SelectedItems" :key="item" class="v9-soft-chip">
                  {{ item }}
                </span>
                <div v-if="!detailData.SelectedItems || !detailData.SelectedItems.length" class="v9-empty-state">
                  未记录特定工作项
                </div>
              </div>
            </div>
          </div>

          <div class="section-v9">
            <div class="v9-section-header">
              <span class="v9-accent"></span>
              <h3>详细描述</h3>
            </div>
            <div class="v9-card description-card">
              <p class="v9-content-text">{{ detailData.Record.CustomContent || '暂无详细描述内容' }}</p>
            </div>
          </div>

          <div v-if="showImage(detailData.Record.MediaFiles).length" class="section-v9">
            <div class="v9-section-header">
              <span class="v9-accent"></span>
              <h3>
                图片
                <small>({{ showImage(detailData.Record.MediaFiles).length }})</small>
              </h3>
            </div>
            <div class="v9-card wr-detail-images">
              <oss-el-image
                v-for="(m, idx) in showImage(detailData.Record.MediaFiles)"
                :key="'dimg-' + idx"
                class="wr-detail-image"
                :src="m"
                :preview-src-list="showImage(detailData.Record.MediaFiles)"
                fit="cover"
              />
            </div>
          </div>

          <div class="section-v9">
            <div class="v9-section-header">
              <span class="v9-accent"></span>
              <h3>
                附件资料
                <small v-if="detailData.Attachments && detailData.Attachments.length">
                  ({{ detailData.Attachments.length }})
                </small>
              </h3>
            </div>
            <div class="v9-attachment-list">
              <div v-for="file in detailData.Attachments" :key="file.Id" class="v9-file-item">
                <div class="v9-file-main" @click="handlePreview(file)">
                  <div class="v9-file-icon">
                    <i :class="getFileIconForAttachment(file)"></i>
                  </div>
                  <div class="v9-file-info">
                    <div
                      class="v9-fn"
                      :title="file.FileName || fileNameFromUrl(file.FileUrl)"
                    >
                      {{ file.FileName || fileNameFromUrl(file.FileUrl) }}
                    </div>
                    <div v-if="formatAttachmentSize(file)" class="v9-fm">{{ formatAttachmentSize(file) }}</div>
                  </div>
                </div>
                <div class="v9-file-actions-group">
                  <el-tooltip content="在线预览" placement="top">
                    <div class="v9-action-btn" @click="handlePreview(file)">
                      <i class="el-icon-view"></i>
                    </div>
                  </el-tooltip>
                  <el-tooltip content="点击下载" placement="top">
                    <oss-media-link :path="file.FileUrl" class="v9-dl-link">
                      <div class="v9-action-btn is-download">
                        <i class="el-icon-download"></i>
                      </div>
                    </oss-media-link>
                  </el-tooltip>
                </div>
              </div>
              <div v-if="!detailData.Attachments || !detailData.Attachments.length" class="v9-empty-attachments">
                <i class="el-icon-document-remove"></i>
                <p>无附件资料</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="onClose">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="previewHintVisible"
      width="480px"
      append-to-body
      custom-class="wr-preview-hint-dialog"
      :show-close="true"
      :close-on-click-modal="false"
      @closed="onPreviewHintClosed"
    >
      <div class="wr-hint">
        <div class="wr-hint__hero">
          <div class="wr-hint__icon" aria-hidden="true">
            <i class="el-icon-warning-outline"></i>
          </div>
          <h3 class="wr-hint__title">预览环境受限</h3>
          <p class="wr-hint__desc">
            当前运行在<strong>私有内网</strong>，微软 Office 在线预览无法访问本地文件，请下载后使用本地 Office 查看。
          </p>
        </div>
        <div class="wr-hint__steps">
          <div class="wr-hint__step">
            <span class="wr-hint__step-no">1</span>
            <div class="wr-hint__step-body">
              <div class="wr-hint__step-title">下载到本地查看</div>
              <div class="wr-hint__step-text">点击详情中的下载按钮，用 Word / Excel / PPT 打开。</div>
            </div>
          </div>
          <div class="wr-hint__step">
            <span class="wr-hint__step-no">2</span>
            <div class="wr-hint__step-body">
              <div class="wr-hint__step-title">公网部署后自动可用</div>
              <div class="wr-hint__step-text">系统部署到公网服务器后，在线预览将自动生效。</div>
            </div>
          </div>
        </div>
        <div v-if="previewHintFile" class="wr-hint__file">
          <i :class="getFileIconForAttachment(previewHintFile)"></i>
          <span class="wr-hint__file-name">{{ previewHintFile.FileName || fileNameFromUrl(previewHintFile.FileUrl) }}</span>
        </div>
      </div>
      <span slot="footer" class="wr-hint__footer">
        <el-button @click="previewHintVisible = false">知道了</el-button>
        <oss-media-link
          v-if="previewHintFile"
          :path="previewHintFile.FileUrl"
          :download="true"
          class="wr-hint__dl-link"
        >
          <el-button type="primary" icon="el-icon-download">下载文件</el-button>
        </oss-media-link>
      </span>
    </el-dialog>

    <el-dialog
      title="文件预览"
      :visible.sync="previewVisible"
      width="96vw"
      top="1vh"
      custom-class="premium-dialog is-preview-mode"
      append-to-body
      :close-on-click-modal="false"
      @closed="onPreviewClosed"
    >
      <div class="preview-container">
        <div class="preview-toolbar">
          <span class="preview-filename">{{ currentPreviewName }}</span>
          <div class="preview-actions">
            <a :href="previewUrlRaw" target="_blank">
              <el-button icon="el-icon-full-screen" type="text">全屏查看</el-button>
            </a>
          </div>
        </div>
        <div class="preview-frame-wrapper">
          <embed
            v-if="previewVisible && previewIsPdf"
            :src="previewUrl"
            type="application/pdf"
            class="preview-iframe preview-iframe--pdf"
          />
          <iframe
            v-else-if="previewVisible"
            :src="previewUrl"
            frameborder="0"
            class="preview-iframe"
          ></iframe>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 工作记录单条详情弹窗（列表 / 统计明细共用）。创建于 2026-07-23
 */
import util from '../../../util/date'
import {
  isAliyunOssHttpsUrl,
  stripOssUrlQuery,
  fileExtFromMedia,
  resolveAttachmentAccessUrl,
} from '../../utils/mediaUrl'
import { getUploadSchoolId } from '../../utils/ossMediaUpload'

export default {
  name: 'WorkRecordDetailDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** 列表行（含 ConfigNames / Attachments / MediaFiles） */
    record: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      previewVisible: false,
      previewIsPdf: false,
      previewUrl: '',
      previewUrlRaw: '',
      currentPreviewName: '',
      previewHintVisible: false,
      previewHintFile: null,
    }
  },
  computed: {
    detailData() {
      if (!this.record) return null
      return {
        Record: this.record,
        SelectedItems: this.record.ConfigNames || this.record.configNames || [],
        Attachments: this.record.Attachments || this.record.attachments || [],
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    formatSimpleDate(time) {
      return time ? util.formatDate.format(new Date(time), 'yyyy-MM-dd') : ''
    },
    async handlePreview(file) {
      if (!file) return
      const rawUrl = file.FileUrl || ''
      const stableUrl = stripOssUrlQuery(rawUrl)
      const fullUrl = this.$resolveImageSrc(rawUrl)
      this.currentPreviewName = file.FileName || this.fileNameFromUrl(rawUrl)

      const ext = fileExtFromMedia(rawUrl, file.FileType)
      const isOffice = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].includes(ext)
      const isPdf = ext === '.pdf'
      const isTxt = ext === '.txt'
      const schoolId = getUploadSchoolId(this)

      const loadAccessUrl = async () => {
        return resolveAttachmentAccessUrl(stableUrl || rawUrl, schoolId, { inline: true })
      }

      if (isOffice) {
        let isInternal = false
        try {
          const url = new URL(fullUrl)
          const host = url.hostname
          isInternal =
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host.startsWith('192.168.') ||
            host.startsWith('10.') ||
            (host.startsWith('172.') &&
              parseInt(host.split('.')[1], 10) >= 16 &&
              parseInt(host.split('.')[1], 10) <= 31)
        } catch (e) {
          isInternal = true
        }

        if (isInternal) {
          this.previewHintFile = file
          this.previewHintVisible = true
          return
        }
        const loading = this.$loading({ lock: true, text: '加载预览...' })
        try {
          const accessUrl = await loadAccessUrl()
          this.previewUrlRaw = accessUrl
          this.previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(accessUrl)}`
          this.previewIsPdf = false
          this.previewVisible = true
        } catch (e) {
          this.$message.error((e && e.message) || '获取预览地址失败，请尝试下载后查看')
        } finally {
          loading.close()
        }
        return
      }

      const needSign = isPdf || isTxt || isAliyunOssHttpsUrl(stableUrl)
      if (needSign) {
        const loading = this.$loading({ lock: true, text: '加载预览...' })
        try {
          const accessUrl = await loadAccessUrl()
          this.previewUrlRaw = accessUrl
          this.previewUrl = accessUrl
          this.previewIsPdf = isPdf
          this.previewVisible = true
        } catch (e) {
          this.$message.error((e && e.message) || '获取预览地址失败，请尝试下载后查看')
        } finally {
          loading.close()
        }
        return
      }

      this.previewUrlRaw = fullUrl
      this.previewUrl = fullUrl
      this.previewIsPdf = isPdf
      this.previewVisible = true
    },
    onPreviewClosed() {
      this.previewIsPdf = false
      this.previewUrl = ''
      this.previewUrlRaw = ''
    },
    onPreviewHintClosed() {
      this.previewHintFile = null
    },
    fileNameFromUrl(url) {
      const clean = String(url || '').split('?')[0]
      const name = clean.split('/').pop() || '附件'
      try {
        return decodeURIComponent(name)
      } catch (e) {
        return name
      }
    },
    getFileIconForAttachment(file) {
      const ext = fileExtFromMedia(file && file.FileUrl, file && file.FileType)
      return this.getFileIcon(ext)
    },
    formatAttachmentSize(file) {
      const size = file && file.FileSize
      if (size == null || size === '' || Number(size) <= 0) return ''
      return `${(Number(size) / 1024).toFixed(1)} KB`
    },
    getFileIcon(ext) {
      const e = (ext || '').toLowerCase()
      if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(e)) return 'el-icon-picture'
      if (['.doc', '.docx'].includes(e)) return 'el-icon-document'
      if (['.xls', '.xlsx'].includes(e)) return 'el-icon-s-grid'
      if (['.ppt', '.pptx'].includes(e)) return 'el-icon-monitor'
      if (e === '.pdf' || e === 'pdf') return 'el-icon-reading'
      if (e === '.txt') return 'el-icon-document-copy'
      return 'el-icon-files'
    },
  },
}
</script>

<style scoped>
.detail-main.v9-edition {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.v9-header {
  padding: 0 0 4px 0;
}
.v9-teacher-info h1 {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.025em;
}
.v9-badges {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.v9-badge-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.v9-bg-indigo {
  background: #eef2ff;
  color: #4f46e5;
}
.v9-bg-emerald {
  background: #ecfdf5;
  color: #10b981;
}
.v9-meta-dot {
  width: 4px;
  height: 4px;
  background: #cbd5e1;
  border-radius: 50%;
}
.v9-class-info,
.v9-date-info {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}
.section-v9 {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.v9-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.v9-accent {
  width: 4px;
  height: 16px;
  background: #4f46e5;
  border-radius: 4px;
}
.v9-section-header h3 {
  font-size: 14px;
  font-weight: 800;
  color: #475569;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.v9-section-header h3 small {
  font-weight: 500;
  color: #94a3b8;
  margin-left: 4px;
}
.v9-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}
.v9-chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.v9-soft-chip {
  background: #f1f5f9;
  color: #334155;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}
.v9-content-text {
  margin: 0;
  line-height: 1.8;
  color: #334155;
  font-size: 15px;
  white-space: pre-wrap;
}
.v9-attachment-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
}
.v9-file-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
}
.v9-file-item:hover {
  border-color: #4f46e5;
  background: #fafbff;
}
.v9-file-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: #f8fafc;
  color: #64748b;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-right: 16px;
}
.v9-file-info {
  flex: 1;
  min-width: 0;
}
.v9-fn {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-all;
}
.v9-fm {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
}
.v9-file-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
}
.v9-file-actions-group {
  display: flex;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
  padding-top: 4px;
}
.v9-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: #f8fafc;
  cursor: pointer;
  border: 1px solid #f1f5f9;
}
.v9-action-btn:hover {
  background: #eef2ff;
  color: #4f46e5;
  border-color: #e0e7ff;
}
.v9-action-btn.is-download:hover {
  background: #ecfdf5;
  color: #10b981;
  border-color: #d1fae5;
}
.v9-dl-link {
  text-decoration: none;
}
.v9-empty-attachments,
.v9-empty-state {
  grid-column: 1 / -1;
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.v9-empty-attachments p {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
}
.wr-detail-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.wr-detail-image {
  width: 100px;
  height: 100px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.wr-hint__hero {
  text-align: center;
  padding: 4px 8px 0;
}
.wr-hint__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: linear-gradient(145deg, #fff7e6, #ffe7ba);
  color: #e6a23c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}
.wr-hint__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}
.wr-hint__desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: #64748b;
}
.wr-hint__steps {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.wr-hint__step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.wr-hint__step-no {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wr-hint__step-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}
.wr-hint__step-text {
  font-size: 13px;
  line-height: 1.55;
  color: #64748b;
}
.wr-hint__file {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px dashed #d0d7e2;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
}
.wr-hint__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wr-hint__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.wr-hint__dl-link {
  text-decoration: none;
}
.animate__animated {
  animation-duration: 0.6s;
  animation-fill-mode: both;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate__fadeIn {
  animation-name: fadeIn;
}
</style>

<style>
.premium-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
}
.premium-dialog .el-dialog__header {
  padding: 16px 24px !important;
  background: #ffffff !important;
  border-bottom: 2px solid #f1f5f9 !important;
}
.premium-dialog .el-dialog__body {
  background: #fcfdfe !important;
  padding: 16px 24px 24px 24px !important;
  max-height: 75vh !important;
  overflow-y: auto !important;
}
.premium-dialog.is-preview-mode {
  width: 96vw !important;
  max-width: none !important;
  margin-top: 1vh !important;
  height: 94vh !important;
  display: flex !important;
  flex-direction: column !important;
}
.premium-dialog.is-preview-mode .el-dialog__body {
  padding: 0 !important;
  flex: 1 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  max-height: none !important;
}
.premium-dialog.is-preview-mode .el-dialog__header {
  padding: 10px 20px !important;
  border-bottom: 1px solid #eee !important;
}
.preview-container {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
}
.preview-toolbar {
  height: 50px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0 20px !important;
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.preview-filename {
  font-weight: 700 !important;
  color: #1e293b !important;
}
.preview-frame-wrapper {
  flex: 1 !important;
  background: #f1f5f9 !important;
}
.preview-iframe {
  width: 100% !important;
  height: 100% !important;
  border: none;
}
.el-dialog.wr-preview-hint-dialog {
  border-radius: 14px !important;
  overflow: hidden !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.14) !important;
}
.el-dialog.wr-preview-hint-dialog .el-dialog__header {
  padding: 14px 18px 0 !important;
  border-bottom: none !important;
  background: #fff !important;
}
.el-dialog.wr-preview-hint-dialog .el-dialog__footer {
  padding: 12px 22px 18px !important;
  border-top: 1px solid #f1f5f9;
  background: #fff !important;
}
</style>
